/**
 * Free-tier reading experiment helpers (2026-06-12 → ~2026-06-26).
 *
 * Whether a user receives a streamed AI interpretation. Paid always does;
 * free users do while the server-controlled 'free_ai_readings' feature flag
 * is on. Spreads, journaling, astro-personalized context, and follow-ups
 * remain paid-only regardless of this flag.
 */
export function hasAiReadingAccess(
  effectiveTier: "free" | "paid",
  freeAiReadingsEnabled: boolean,
): boolean {
  return effectiveTier === "paid" || freeAiReadingsEnabled;
}

/**
 * The "This reading is AI-generated" footnote belongs only under text the AI
 * actually wrote — never under a limit/connection message that ended the
 * reading (those are appended into the same interpretation text).
 */
export function showAiDisclaimer(
  interpretation: string,
  isStreaming: boolean,
  streamErrored: boolean,
): boolean {
  return interpretation.length > 0 && !isStreaming && !streamErrored;
}

/** Home greeting line for free users — must agree with the live pull limit. */
export function pullsGreeting(limit: number, remaining: number): string {
  if (limit <= 1) {
    return "One pull today. Make it count — or save it for tonight.";
  }
  if (remaining <= 0) {
    return `All ${limit} pulls used today. Fresh cards at midnight.`;
  }
  return `${remaining} of ${limit} pulls left today. Make them count — or save one for tonight.`;
}

/**
 * Headline for a screen that refuses to draw because the user is OUT of
 * pulls (remaining <= 0) — e.g. ReadingPage gating a new draw the server
 * would reject. This is deliberately NOT `pullsGreeting`: at the locked
 * default (limit 1), `pullsGreeting`'s limit<=1 branch always says "One pull
 * today. Make it count" regardless of whether that pull was spent — correct
 * for HomePage's greeting (locked by freeReadings.test.ts), wrong on a
 * refusal screen that just told the user no card is coming.
 *
 * Above limit 1, `pullsGreeting` already says the right thing at remaining
 * <= 0 ("All N pulls used today..."), so this defers to it there.
 *
 * At limit <= 1, this mirrors the server's own out-of-pulls copy
 * (apps/api/src/lib/pull-limit.ts `pullLimitMessage(1)`), trimmed of its
 * trailing "Upgrade to Cosmic for unlimited readings!" sentence — redundant
 * next to an Upgrade button already on the same screen. Do not invent new
 * wording here; keep it in sync with the server string if that ever changes.
 */
export function outOfPullsHeadline(limit: number, remaining: number): string {
  if (limit <= 1) {
    return "You've used your free pull for today.";
  }
  return pullsGreeting(limit, remaining);
}
